import axios from 'axios';

// Use localhost for local development
// Set REACT_APP_API_BASE_URL to override; set to 'SAME_ORIGIN' to force relative URLs explicitly
const ENV_BASE = process.env.REACT_APP_API_BASE_URL;
export const API_BASE_URL = ENV_BASE === 'SAME_ORIGIN' ? '' : (ENV_BASE || '');

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true
});

// Request interceptor to automatically add admin authentication headers
apiClient.interceptors.request.use(
  (config) => {
    // Add admin authentication headers if available
    const adminToken = localStorage.getItem('adminToken');
    const adminApiKey = localStorage.getItem('adminApiKey') || localStorage.getItem('ADMIN_API_KEY');
    
    if (adminToken) {
      config.headers['Authorization'] = `Bearer ${adminToken}`;
    }
    
    if (adminApiKey) {
      config.headers['x-admin-key'] = adminApiKey;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Log requests/responses for easier debugging
apiClient.interceptors.response.use(
  (res) => res,
  (error) => {
    const cfg = error?.config || {};
    const url = cfg?.baseURL ? `${cfg.baseURL}${cfg.url || ''}` : (cfg.url || '');
    /* eslint-disable no-console */
    console.error('API Error:', {
      method: (cfg.method || 'get').toUpperCase(),
      url,
      status: error?.response?.status,
      data: error?.response?.data,
      message: error?.message
    });
    /* eslint-enable no-console */
    return Promise.reject(error);
  }
);

export const uploadClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 300000,
  withCredentials: true
});

uploadClient.interceptors.response.use(
  (res) => res,
  (error) => {
    const cfg = error?.config || {};
    const url = cfg?.baseURL ? `${cfg.baseURL}${cfg.url || ''}` : (cfg.url || '');
    /* eslint-disable no-console */
    console.error('Upload API Error:', {
      method: (cfg.method || 'post').toUpperCase(),
      url,
      status: error?.response?.status,
      data: error?.response?.data,
      message: error?.message
    });
    /* eslint-enable no-console */
    return Promise.reject(error);
  }
);

export const endpoints = {
  registerVendor: '/api/vendors/register',
  loginVendor: '/api/vendors/login',
  loginAdmin: '/api/admin/login',
  forgotPassword: '/api/vendor/forgot-password',
  resetPassword: '/api/vendor/reset-password',
  updateLocation: (id) => `/api/vendors/${id}/location`,
  uploadKyc: (id) => `/api/vendors/${id}/kyc`,
  kycStatus: (id) => `/api/vendors/${id}/kyc-status`,
  kycDocs: (id) => `/api/vendors/${id}/kyc-docs`,
  deleteKycDoc: (vendorId, docId) => `/api/vendors/${vendorId}/kyc-docs/${docId}`,
  
  // New comprehensive KYC endpoints
  kycDashboard: (id) => `/api/kyc/dashboard/${id}`,
  kycDocuments: (id) => `/api/kyc/documents/${id}`,
  kycUpload: (id) => `/api/kyc/upload/${id}`,
  kycDocumentDelete: (docId) => `/api/kyc/document/${docId}`,
  kycDocumentDownload: (docId) => `/api/kyc/document/${docId}/download`,
  kycNotifications: (id) => `/api/kyc/notifications/${id}`,
  kycNotificationRead: (notificationId) => `/api/kyc/notifications/${notificationId}/read`,
  kycTemplates: '/api/kyc/templates',
  kycOverallStatus: (id) => `/api/kyc/status/${id}`,
  adminDashboardSummary: '/api/admin/dashboard/summary',
  adminInventorySummary: '/api/admin/inventory/summary',
  adminVendors: '/api/admin/vendors',
  adminPayouts: '/api/admin/payouts',
  adminKycDocuments: '/api/admin/kyc-documents',
  adminKycDocumentStatus: (docId) => `/api/admin/kyc-documents/${docId}/status`,
  adminKycRunOCR: (docId) => `/api/admin/kyc/${docId}/ocr`,
  adminKycVerify: (docId) => `/api/admin/kyc/${docId}/verify`,
  adminKycAudit: '/api/admin/kyc-audit',
  adminKycDownload: (docId) => `/api/admin/documents/${docId}/download`,
  adminKycSummary: '/api/admin/kyc-summary',
  // Admin Reports
  adminReport_VendorsActiveInactive: '/api/admin/reports/vendors/active-inactive',
  adminReport_VendorPerformance: '/api/admin/reports/vendors/performance',
  adminReport_VendorApprovalHistory: '/api/admin/reports/vendors/approval-history',
  adminReport_ProductSalesRevenue: '/api/admin/reports/products/sales-revenue',
  adminReport_LowStock: '/api/admin/reports/products/low-stock',
  adminReport_TopLeastProducts: '/api/admin/reports/products/top-least',
  adminReport_ProductPriceHistory: '/api/admin/reports/products/price-history',
  adminReport_StockMovements: '/api/admin/reports/inventory/stock-movements',
  adminReport_AvailableVsReserved: '/api/admin/reports/inventory/available-vs-reserved',
  adminReport_HistoricalStock: '/api/admin/reports/inventory/historical-stock',
  adminReport_OrdersByStatus: '/api/admin/reports/orders/by-status',
  adminReport_PaymentStatus: '/api/admin/reports/orders/payment-status',
  adminReport_OrdersAggregate: '/api/admin/reports/orders/aggregate',
  adminReport_KycVerificationStatus: '/api/admin/reports/kyc/verification-status',
  adminReport_KycExpiringDocuments: '/api/admin/reports/kyc/expiring-documents',
  adminReport_KycAuditLog: '/api/admin/reports/kyc/audit-log',
  // Admin Reports: Export
  adminReport_Export: '/api/admin/reports/export/:type',
  // Drill-down endpoints (use existing resources where possible)
  adminOrders: '/api/admin/orders',
  adminOrderById: (id) => `/api/admin/orders/${id}`,
  adminProducts: '/api/admin/products',
  adminProductPriceHistory: (id) => `/api/admin/products/${id}/price-history`,
  adminVendorsList: '/api/admin/vendors',
  // Aliases per requested routes
  adminDocApprove: (docId) => `/api/admin/documents/${docId}/approve`,
  adminDocReject: (docId) => `/api/admin/documents/${docId}/reject`,
  adminDocOCR: (docId) => `/api/admin/documents/${docId}/ocr`,
  adminDocVerify: (docId) => `/api/admin/documents/${docId}/verify`,
  adminDocDelete: (docId) => `/api/admin/documents/${docId}/delete`,
  // Payments
  paymentsList: '/api/payments',
  paymentById: (id) => `/api/payments/${id}`,
  paymentRefund: (id) => `/api/payments/${id}/refund`,
  paymentReprocess: (id) => `/api/payments/${id}/reprocess`,
  paymentsBulk: '/api/payments/bulk',
};

export default apiClient;


